/**
 * Phase G, G3 — the real i18next instance. Loaded by t.ts (the ~800
 * existing call sites keep calling the plain `t(key, vars)` function
 * exported from there — this file is the machinery underneath it, not a
 * new call-site API).
 *
 * Library choice: react-i18next + i18next. Verified current at install
 * time (Phase G): i18next 23.16.8, react-i18next 14.1.3, both actively
 * maintained, both compatible with this app's React 18 + Vite 5 stack.
 * Chosen over alternatives (react-intl / LinguiJS / a bespoke resolver)
 * because: (1) native CLDR plural-rule support via `Intl.PluralRules`
 * (G10) — no separate plural library needed; (2) a first-party lazy-
 * loading story (a custom `backend` plugin, used below) that fits this
 * app's existing "one flat JSON per namespace" catalogue shape without
 * forcing ICU MessageFormat syntax onto ~1120 already-written strings;
 * (3) the most mature RTL ecosystem of the three (G4/G5 leans on plain
 * `dir` attributes + Tailwind logical properties, which is
 * library-agnostic, but i18next's `dir()` helper and its interpolation
 * config are what make keeping the exact "{var}" / "ns:key.path"
 * catalogue format possible at all — see the init() call below).
 *
 * G1 — the admin namespace is structurally excluded from every
 * non-English language here (see `lazyLanguageBackend.read` below): no
 * non-English language ever loads real content for it, so it always
 * falls through to the English resource via `fallbackLng`. That is the
 * ONE central place this exclusion lives — LearnerLayout additionally
 * never lets the `dir`/`lang` attributes reach /admin at all, so admin
 * gets two independent, structural guarantees (never-translated content,
 * never-RTL layout), neither of which depends on any admin file
 * "remembering" not to call a translated string.
 */
// Namespace import, not a default import: this repo's ts-jest config
// (frontend/tsconfig.json) doesn't set esModuleInterop, and i18next's own
// CJS build (dist/cjs/i18next.js) doesn't set `__esModule` either — under
// that combination, `import i18n from 'i18next'` resolves to `undefined`
// at test time (TS emits a bare `.default` property access with no
// interop helper), even though the exact same code works fine under
// Vite/esbuild (which handles CJS/ESM interop itself, independent of the
// tsconfig flag). A namespace import plus an explicit `.default` fallback
// works correctly in both environments without touching the shared
// tsconfig (which other files also rely on).
import * as i18nextNS from 'i18next';
// Type-only import — erased entirely at compile time, no `require()` is
// ever emitted for it, so it can't hit the interop problem described
// above. `i18n` (lowercase) is the actual instance type; the namespace
// import's own inferred type (`typeof i18nextNS`) conflates the module's
// static exports (`createInstance`, `use`, ...) with the default export
// and is missing instance members like `.language`/`.changeLanguage`.
import type { i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import enCatalog from './catalog/en';
import { DEFAULT_LANGUAGE, LANGUAGES } from './languages';

const i18n: I18nInstance =
  (i18nextNS as unknown as { default?: I18nInstance }).default ?? (i18nextNS as unknown as I18nInstance);

export const NAMESPACES = [
  'account', 'activities', 'admin', 'auth', 'common', 'errors',
  'lesson', 'messages', 'onboarding', 'placement', 'profile', 'speaking', 'subscription', 'writing',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

/** G1 — never loaded for any language other than English. */
export const ADMIN_ONLY_NAMESPACES: readonly Namespace[] = ['admin'];
export const LEARNER_NAMESPACES: readonly Namespace[] = NAMESPACES.filter(
  (ns) => !ADMIN_ONLY_NAMESPACES.includes(ns),
);

type FlatCatalog = Record<string, string>;
type LanguageBundle = Partial<Record<Namespace, FlatCatalog>>;

// One dynamic import() per non-English language — Vite code-splits each
// into its own chunk automatically (this is the same "dynamic import with
// an interpolated path" mechanism Vite's own docs recommend for exactly
// this use case). Plain import(), NOT import.meta.glob: import.meta has
// no CommonJS representation and is a hard syntax error under this
// repo's ts-jest config (see t.ts's own long-standing comment on the
// identical constraint) — a template-literal import() call, by contrast,
// compiles to a Promise-wrapped require() under ts-jest's CommonJS
// output and to a real lazy chunk under Vite, so one line works in both
// environments.
const languageBundleCache = new Map<string, Promise<LanguageBundle>>();
function loadLanguageBundle(language: string): Promise<LanguageBundle> {
  let pending = languageBundleCache.get(language);
  if (!pending) {
    pending = import(`./catalog/lang/${language}/index.ts`).then(
      (mod) => mod.default as LanguageBundle,
    );
    languageBundleCache.set(language, pending);
  }
  return pending;
}

const lazyLanguageBackend = {
  type: 'backend' as const,
  init() {
    /* no options needed */
  },
  read(
    language: string,
    namespace: string,
    callback: (err: unknown, data: FlatCatalog | null) => void,
  ) {
    if (language === DEFAULT_LANGUAGE) {
      // English is imported statically below and bundled eagerly with
      // the rest of the app (G3's explicit requirement: English ships
      // eager, the other 21 languages never do) — it must never go
      // through import(), so it never gets its own async chunk.
      callback(null, (enCatalog as Record<string, FlatCatalog>)[namespace] ?? {});
      return;
    }
    if ((ADMIN_ONLY_NAMESPACES as string[]).includes(namespace)) {
      // G1 — structural exclusion: no non-English language ever loads
      // the admin namespace's content, even if a future translation file
      // happened to define one. i18next's fallbackLng then serves the
      // (always-current) English admin.json content instead.
      callback(null, {});
      return;
    }
    loadLanguageBundle(language)
      .then((bundle) => callback(null, bundle[namespace as Namespace] ?? {}))
      .catch((err) => {
        // No catalogue exists yet for this language (or this namespace
        // isn't translated yet within it) — i18next's fallbackLng then
        // serves the already-loaded English resource. This is the
        // expected, common case today: only `ar` has any real (partial,
        // proof-of-concept — see catalog/lang/ar/) content; the other 20
        // languages in languages.ts resolve entirely through this
        // fallback until a future translation-generation round.
        callback(err, null);
      });
  },
};

let initPromise: Promise<I18nInstance> | null = null;

export function initI18n(): Promise<I18nInstance> {
  if (initPromise) return initPromise;
  initPromise = i18n
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .use(lazyLanguageBackend as any)
    .use(initReactI18next)
    .init({
      lng: DEFAULT_LANGUAGE,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: LANGUAGES.map((l) => l.code),
      nonExplicitSupportedLngs: true,
      ns: [...NAMESPACES],
      defaultNS: 'common',
      // Preserves the exact existing "namespace:key.path" call-site
      // convention untouched (see t.ts) — the ~800 existing t('ns:key')
      // call sites needed ZERO rewriting for this migration.
      // `keySeparator: false` stops i18next from treating the literal
      // dots inside "key.path" as nested-object path segments (the
      // catalogue's own flat, dotted-key format, unchanged since Phase
      // F — see catalog/README.md); `nsSeparator: ':'` matches the colon
      // every call site already uses.
      keySeparator: false,
      nsSeparator: ':',
      // The catalogue's placeholders are single-brace ("{name}"), not
      // i18next's own double-brace default — matched exactly so no
      // catalogue string needed rewriting either.
      interpolation: {
        escapeValue: false, // React already escapes
        prefix: '{',
        suffix: '}',
      },
      react: { useSuspense: false },
      // English resolves synchronously inside `read()` above (no real
      // async work), so with initImmediate:false the whole init()
      // resolves synchronously too — t() works immediately on import,
      // matching Phase F's resolver's own synchronous behavior exactly.
      initImmediate: false,
      returnEmptyString: false,
      parseMissingKeyHandler: (key: string) => {
        console.warn(`[i18n] missing catalog key: ${key}`);
        return key;
      },
    })
    // i18next's own .init() typings resolve the returned Promise with the
    // main `t` function, not the instance — .then() here re-resolves to
    // the instance itself, matching this function's declared return type
    // and what every caller (t.ts, LanguageContext.tsx) actually wants.
    .then(() => i18n);
  return initPromise;
}

export default i18n;
